import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Hidden5SReport from './Hidden5SReport';
import Acumen_25_Years_LOGO from '../assets/Acumen_25_Years_LOGO.png';

// ── Theme tokens (matches Hidden5SReport T object) ────────────────────────────
const T = {
  navy: '#A81026',
  blue: '#C4163A',
  teal: '#7B0F2B',
};

// ── 5S Style colour tokens ────────────────────────────────────────────────────
const STYLE_COLORS = {
  Signaling:           { color: '#3b5ec0', bg: '#f0f4ff', border: '#bfdbfe', fillStart: '#1e3a8a', fillEnd: '#3b82f6' },
  Stimulating:         { color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', fillStart: '#14532d', fillEnd: '#22c55e' },
  Suggesting:          { color: '#d9790a', bg: '#fffbeb', border: '#fde68a', fillStart: '#b45309', fillEnd: '#f59e0b' },
  Sharing_Sanctioning: { color: '#7c3aed', bg: '#faf5ff', border: '#e9d5ff', fillStart: '#4c1d95', fillEnd: '#a78bfa' },
};

// ── Adaptability tier helper ──────────────────────────────────────────────────
const ADAPT_ROWS = [
  {
    tier: 'low',
    pill: 'Low (Below 4)',
    pc: '#a90c2c', pb: '#fdf0f2', border: '#f5c6cf',
    desc: 'You currently rely on a limited range of leadership styles and may find it challenging to adjust to different situations. Building awareness of alternative approaches and practising flexibility will significantly strengthen your effectiveness.',
  },
  {
    tier: 'midlow',
    pill: 'Mid to Low (5–10)',
    pc: '#92400e', pb: '#fffbeb', border: '#fde68a',
    desc: 'You show some adaptability but often default to familiar styles. Expanding your range and intentionally applying different approaches will improve your impact across varied situations.',
  },
  {
    tier: 'midhigh',
    pill: 'Mid to High (11–15)',
    pc: '#166534', pb: '#f0fdf4', border: '#bbf7d0',
    desc: 'You demonstrate good adaptability and can adjust your style in many situations. With continued practice, you can become even more fluid and intentional in your approach.',
  },
  {
    tier: 'high',
    pill: 'High (16 and above)',
    pc: '#1f2e54', pb: '#f0f3fb', border: '#c7d2f0',
    desc: 'You are highly adaptable — you read situations well and shift your leadership style effectively to meet the needs of others.',
  },
];

const getAdaptTier = (s) =>
  s >= 16 ? 'high' : s >= 11 ? 'midhigh' : s >= 5 ? 'midlow' : 'low';

// ── Style definition content ──────────────────────────────────────────────────
const STYLE_DEFS = [
  {
    key: 'signaling',
    dominantKey: 'Signaling',
    color: STYLE_COLORS.Signaling.color,
    bg: STYLE_COLORS.Signaling.bg,
    border: STYLE_COLORS.Signaling.border,
    emoji: '📡',
    label: 'Signaling — Leadership Style',
    archetype: 'The Directing Leader',
    tagline: 'You lead through clarity and structure.',
    conceptual:
      'Leaders who score high on Signaling are highly attuned to context and environment. They read the room, pick up on unspoken cues, and communicate through visible actions, symbols, and presence as much as through words. Their leadership is characterised by deliberate messaging and purposeful visibility.',
    strengths: [
      'You bring order and clarity to ambiguous situations.',
      'People feel secure and focused under your guidance.',
      'You excel in high-stakes, time-sensitive, or new-team environments.',
    ],
    growth:
      'As your team members grow in competence and confidence, experiment with asking their opinion before giving direction. Moving up the leadership ladder — even occasionally — can unlock greater ownership and initiative in your people.',
    quote: 'Structure is a gift — but autonomy is the next level.',
    cognitiveTitle: 'Cognitive Characteristics',
    cognitive: [
      'Process their environment holistically, noticing patterns, energy shifts, and unspoken dynamics before others articulate them.',
      'Prefer to lead by example and demonstration, believing actions carry more weight than instructions.',
      'Tend to think in frameworks, naturally translating complex ideas into clear signals and cues for their teams.',
    ],
    behaviorTitle: 'Behavioural Indicators',
    behavior: [
      'Communicate priorities through visible behaviours — how they spend time, who they recognise, what they address publicly.',
      'Set the cultural tone of a team through consistent, deliberate non-verbal and symbolic cues.',
      'In meetings, listen intently before speaking; when they do speak, their words carry disproportionate weight.',
    ],
  },
  {
    key: 'stimulating',
    dominantKey: 'Stimulating',
    color: STYLE_COLORS.Stimulating.color,
    bg: STYLE_COLORS.Stimulating.bg,
    border: STYLE_COLORS.Stimulating.border,
    emoji: '⚡',
    label: 'Stimulating — Leadership Style',
    archetype: 'The Motivating Leader',
    tagline: 'You lead through encouragement and energy.',
    conceptual:
      'Leaders with a Stimulating style naturally blend direction with motivation. They don\'t just tell people what to do — they spark willingness to act, ask stretching questions, build confidence, and get people to step up.',
    strengths: [
      'You build team morale and a sense of purpose.',
      'You help hesitant or developing team members find their footing.',
      'You balance structure with emotional connection.',
    ],
    growth:
      'Watch for moments when your encouragement might substitute for honest feedback. The most effective Stimulating leaders know when to shift from cheerleading to coaching — and when to challenge the team to go further independently.',
    quote: 'You light the fire — now let them tend it.',
    cognitiveTitle: 'Cognitive Characteristics',
    cognitive: [
      'Naturally drawn to complexity, challenge, and unexplored possibility — energised by problems that others find daunting.',
      'Think expansively, connecting seemingly unrelated ideas to surface new approaches.',
      'Process information quickly and enjoy dialogue as a mode of thinking — they refine ideas through debate.',
    ],
    behaviorTitle: 'Behavioural Indicators',
    behavior: [
      'Regularly ask provocative or stretch questions designed to expand thinking.',
      'Create forums where ideas are challenged respectfully and bold thinking is rewarded.',
      'Attract high-potential talent because they create environments where growth is expected.',
    ],
  },
  {
    key: 'suggesting',
    dominantKey: 'Suggesting',
    color: STYLE_COLORS.Suggesting.color,
    bg: STYLE_COLORS.Suggesting.bg,
    border: STYLE_COLORS.Suggesting.border,
    emoji: '💡',
    label: 'Suggesting — Leadership Style',
    archetype: 'The Guiding Leader',
    tagline: 'You lead through questions and co-creation.',
    conceptual:
      'Leaders with a Suggesting style naturally invite input before making decisions. They treat capable team members as thinking partners, creating space for ideas to emerge from the team rather than always originating from themselves.',
    strengths: [
      'You develop problem-solving skills in your team.',
      'You build buy-in by involving people in the process.',
      'You create psychologically safe environments for ideas and feedback.',
    ],
    growth:
      'Be mindful of situations where team members need firmer direction — especially in moments of uncertainty or crisis. Not every situation calls for collaborative exploration. Knowing when to step down the ladder and Signal clearly is equally important.',
    quote: 'The best questions create the best answers — and the best teams.',
    cognitiveTitle: 'Cognitive Characteristics',
    cognitive: [
      'Prefer to influence through well-timed, well-framed recommendations rather than mandates.',
      'Strong analytical thinkers who invest time in understanding context before offering input.',
      'Patient communicators; allow ideas to develop through conversation, comfortable with ambiguity.',
    ],
    behaviorTitle: 'Behavioural Indicators',
    behavior: [
      'Frequently act as sounding boards; colleagues often seek them out for perspective.',
      'Frame recommendations indirectly, inviting others to arrive at conclusions.',
      'Build influence quietly over time through the quality of decisions teams make after consulting them.',
    ],
  },
  {
    key: 'sharing_sanctioning',
    dominantKey: 'Sharing & Sanctioning',
    color: STYLE_COLORS.Sharing_Sanctioning.color,
    bg: STYLE_COLORS.Sharing_Sanctioning.bg,
    border: STYLE_COLORS.Sharing_Sanctioning.border,
    emoji: '🤝',
    label: 'Sharing & Sanctioning — Leadership Style',
    archetype: 'The Empowering Leader',
    tagline: 'You lead through trust, partnership, and full delegation.',
    conceptual:
      'Leaders with a Sharing & Sanctioning style are builders of shared ownership and collective accountability. They are highly effective at creating alignment, distributing authority, and ensuring that standards and expectations are held clearly across the team.',
    strengths: [
      'You build highly autonomous, self-directed teams.',
      'You develop future leaders by giving them real authority.',
      'Your team members grow faster under your leadership.',
    ],
    growth:
      'Be intentional about who you delegate to — and when. Sharing & Sanctioning works best with capable, confident individuals. With newer or struggling team members, stepping down the ladder to Signal or Stimulate is not a weakness — it\'s exactly the right call.',
    quote: 'The greatest leaders make themselves unnecessary — by making others extraordinary.',
    cognitiveTitle: 'Cognitive Characteristics',
    cognitive: [
      'Value collective wisdom and shared decision-making; outcomes improve when more perspectives are incorporated.',
      'Strong at designing processes and rituals that embed shared norms across the team.',
      'Highly attuned to fairness, equity, and consistency.',
    ],
    behaviorTitle: 'Behavioural Indicators',
    behavior: [
      'Actively distribute ownership, ensuring team members understand their authority and responsibilities.',
      'Create transparent frameworks for decision-making so teams understand how and why choices are made.',
      'Build highly cohesive teams by investing in shared values, rituals, and common language.',
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const FivesResult = () => {
  const location = useLocation();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfData, setPdfData] = useState(null);
  const [scriptsReady, setScriptsReady] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // ── Load html2canvas + jsPDF eagerly on mount ─────────────────────────────
  useEffect(() => {
    const loadScript = (src, id) =>
      new Promise((resolve) => {
        if (document.getElementById(id) && window.html2canvas && window.jspdf) {
          resolve(); return;
        }
        if (document.getElementById(id)) {
          // Already in DOM but maybe not ready — poll briefly
          const t = setInterval(() => {
            if (window.html2canvas && window.jspdf?.jsPDF) { clearInterval(t); resolve(); }
          }, 100);
          setTimeout(() => { clearInterval(t); resolve(); }, 8000);
          return;
        }
        const s = document.createElement('script');
        s.id = id; s.src = src;
        s.onload = () => resolve();
        s.onerror = () => resolve(); // resolve anyway so we can show an error later
        document.head.appendChild(s);
      });

    Promise.all([
      loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js', 'h2c-5s-v1'),
      loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js', 'jspdf-5s-v1'),
    ]).then(() => setScriptsReady(true));
  }, []);

  // ── Trigger Hidden5SReport PDF generation ────────────────────────────────
  const handleDownloadPDF = () => {
    if (!scriptsReady) {
      alert('PDF libraries are still loading, please try again in a moment.');
      return;
    }
    if (isGenerating) return;
    const normalized = reportData?.data || reportData;
    setIsGenerating(true);
    setPdfData(normalized);
  };

  useEffect(() => {
    if (location.state?.scores) {
      setReportData(location.state);
      setLoading(false);
    } else {
      const fetchFallback = async () => {
        try {
          const email = location.state?.email;
          const phone = location.state?.phone;
          const response = await fetch('response/calculate-score-5s/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, phone }),
          });
          if (!response.ok) throw new Error('Could not retrieve report data.');
          const result = await response.json();
          setReportData(result.data);
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };
      fetchFallback();
    }
  }, [location.state]);

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', fontFamily: 'Satoshi, sans-serif' }}>Loading Report...</div>;
  if (error)   return <div style={{ padding: '2rem', color: 'red',         fontFamily: 'Satoshi, sans-serif' }}>Error: {error}</div>;
  if (!reportData) return <div style={{ padding: '2rem',                   fontFamily: 'Satoshi, sans-serif' }}>No data found. Please retake the survey.</div>;

  // ── Normalise data shape ──────────────────────────────────────────────────
  const normalized  = reportData?.data || reportData;
  const name        = normalized?.name        || normalized?.userInfo?.name || '—';
  const email       = normalized?.email       || normalized?.userInfo?.email || '';
  const phone       = normalized?.phone       || normalized?.userInfo?.phone || '';
  const scores      = normalized?.scores      || {};
  const submission_time = normalized?.submission_time;

  const sig   = scores.Signaling           || 0;
  const stim  = scores.Stimulating         || 0;
  const sug   = scores.Suggesting          || 0;
  const shar  = scores.Sharing_Sanctioning || 0;
  const adapt = scores.Total_Adaptability  || 0;

  const total    = (sig + stim + sug + shar) || 12;
  const sigPct   = ((sig  / total) * 100).toFixed(1) + '%';
  const stimPct  = ((stim / total) * 100).toFixed(1) + '%';
  const sugPct   = ((sug  / total) * 100).toFixed(1) + '%';
  const sharPct  = ((shar / total) * 100).toFixed(1) + '%';
  const adaptBarPct = (((adapt + 24) / 48) * 100).toFixed(1) + '%';

  const dateStr = submission_time
    ? new Date(submission_time).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    : new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

  // ── Dominant style ────────────────────────────────────────────────────────
  const styleKeyScores = [
    { key: 'Signaling',           val: sig,  label: 'Signaling'            },
    { key: 'Stimulating',         val: stim, label: 'Stimulating'          },
    { key: 'Suggesting',          val: sug,  label: 'Suggesting'           },
    { key: 'Sharing_Sanctioning', val: shar, label: 'Sharing & Sanctioning'},
  ];
  const dominantStyle = styleKeyScores.reduce((a, b) => (b.val > a.val ? b : a)).label;

  // Sorted highest-score-first — used only on the "Leadership Style Definitions" pages
  const STYLE_DEFS_SORTED = [...STYLE_DEFS].sort((a, b) => {
    const aVal = styleKeyScores.find((s) => s.label === a.dominantKey)?.val ?? -Infinity;
    const bVal = styleKeyScores.find((s) => s.label === b.dominantKey)?.val ?? -Infinity;
    return bVal - aVal;
  });

  // ── Summary texts ─────────────────────────────────────────────────────────
  const resultText =
    `Based on your responses, your primary leadership influence style is ${dominantStyle}. ` +
    (dominantStyle === 'Signaling'
      ? 'You lead through presence, action, and deliberate communication — your team reads your cues and takes direction from what you visibly prioritise and model.'
      : dominantStyle === 'Stimulating'
      ? 'You lead by raising energy, ambition, and intellectual challenge — your natural mode is to energise and stretch the people around you toward greater performance.'
      : dominantStyle === 'Suggesting'
      ? 'You lead through trusted insight and well-timed counsel — your influence is built on the quality of your perspective and your skill in guiding others toward sound conclusions.'
      : 'You lead through shared ownership and collective accountability — your strength is in aligning people around clear expectations and ensuring that standards are held consistently.');

  const practicalLeadershipText =
    dominantStyle === 'Signaling'
      ? 'For this participant, the most effective leadership development path involves building on the Signaling foundation while deliberately practising the complementary styles in low-risk settings. Work with a coach or peer to identify situations where a different style would yield a better outcome — then experiment intentionally.'
      : dominantStyle === 'Stimulating'
      ? 'For this participant, the most effective development path involves channelling Stimulating energy while building comfort with stepping back when needed. Pairing high-energy coaching with deliberate listening and reflection practices will expand leadership range significantly.'
      : dominantStyle === 'Suggesting'
      ? 'For this participant, the most effective development path involves trusting your counsel while building confidence in more directive moments. Practise stepping down the ladder in situations that require clear, firm guidance — your team will benefit from both your insight and your decisiveness.'
      : 'For this participant, the strongest leadership approach should draw on the Sharing & Sanctioning foundation while building awareness of when the team needs more structure. Identify emerging team members who still need guidance and practise flexing toward Signal and Stimulate for them.';

  // ── Adaptability tier ─────────────────────────────────────────────────────
  const currentTierKey = getAdaptTier(adapt);
  const currentTierRow = ADAPT_ROWS.find((r) => r.tier === currentTierKey);
  const tierLabelStyle = {
    fontSize: '10px', fontWeight: 700, textTransform: 'uppercase',
    padding: '5px 12px', borderRadius: '999px',
    background: currentTierRow.pb, color: currentTierRow.pc,
    border: `1px solid ${currentTierRow.border}`,
  };

  // ── renderStyleCard ───────────────────────────────────────────────────────
  const renderStyleCard = (style) => {
    const isHighlighted = style.dominantKey === dominantStyle;
    return (
      <div
        key={style.key}
        className="ss-item"
        style={{
          borderTop:    `1px solid var(--line)`,
          borderRight:  `1px solid var(--line)`,
          borderBottom: `1px solid var(--line)`,
          borderLeft:   `5px solid ${style.color}`,
          marginBottom: '1.5rem',
          position: 'relative',
          ...(isHighlighted && {
            background:   style.bg,
            borderTop:    `1.5px solid ${style.border}`,
            borderRight:  `1.5px solid ${style.border}`,
            borderBottom: `1.5px solid ${style.border}`,
            borderLeft:   `5px solid ${style.color}`,
            boxShadow:    `0 0 0 3px ${style.border}`,
          }),
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
          <div style={{
            width: '40px', height: '40px', flexShrink: 0,
            borderRadius: '10px', background: '#fff',
            border: `1px solid ${style.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '20px',
          }}>
            {style.emoji}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
              <div style={{ fontSize: '9px', fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: style.color, marginBottom: '2px' }}>
                {style.label}
              </div>
              {isHighlighted && (
                <div style={{
                  flexShrink: 0,
                  background: style.color, color: '#fff',
                  fontSize: '10px', fontWeight: 800,
                  padding: '3px 10px', borderRadius: '999px',
                  textTransform: 'uppercase', letterSpacing: '.06em',
                  whiteSpace: 'nowrap',
                }}>
                  ★ Your Style
                </div>
              )}
            </div>
            <div style={{ fontSize: '18px', fontWeight: 700, color: '#0d0e10', lineHeight: 1.15 }}>
              {style.archetype}
            </div>
            <div style={{ fontSize: '12px', fontStyle: 'italic', color: '#4b5068', marginTop: '2px' }}>
              "{style.tagline}"
            </div>
          </div>
        </div>

        {/* Conceptual definition */}
        <p style={{ margin: '0 0 0.75rem', color: '#334155', fontSize: '0.9rem', lineHeight: 1.65 }}>
          <strong>Conceptual Definition:</strong> {style.conceptual}
        </p>

        {/* Cognitive + Behavioral side by side */}
        <div className="ss-cols" style={{ marginTop: '0.75rem' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '9px', fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: '#4b5068', marginBottom: '6px' }}>
              {style.cognitiveTitle}
            </div>
            <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.84rem', color: '#475569', lineHeight: 1.6 }}>
              {style.cognitive.map((pt, i) => <li key={i} style={{ marginBottom: '0.3rem' }}>{pt}</li>)}
            </ul>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '9px', fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: '#4b5068', marginBottom: '6px' }}>
              {style.behaviorTitle}
            </div>
            <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.84rem', color: '#475569', lineHeight: 1.6 }}>
              {style.behavior.map((pt, i) => <li key={i} style={{ marginBottom: '0.3rem' }}>{pt}</li>)}
            </ul>
          </div>
        </div>

        {/* Strengths + Growth */}
        <div className="ss-cols" style={{ marginTop: '0.85rem' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '9px', fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: '#4b5068', marginBottom: '6px' }}>
              Strengths
            </div>
            {style.strengths.map((st, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '7px', marginBottom: '5px' }}>
                <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: style.color, flexShrink: 0, marginTop: '5px' }} />
                <span style={{ fontSize: '0.84rem', color: '#0d0e10', lineHeight: 1.5 }}>{st}</span>
              </div>
            ))}
          </div>
          <div style={{ flex: 1, background: style.bg, border: `1px solid ${style.border}`, borderRadius: '10px', padding: '12px 14px' }}>
            <div style={{ fontSize: '9px', fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: style.color, marginBottom: '6px' }}>
              Growth Edge
            </div>
            <p style={{ fontSize: '0.84rem', color: '#0d0e10', lineHeight: 1.6, margin: 0 }}>{style.growth}</p>
          </div>
        </div>

        {/* Quote */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', paddingTop: '12px', marginTop: '10px', borderTop: '1px solid #e4e8f0' }}>
          <div style={{ width: '3px', flexShrink: 0, height: '30px', background: style.color, borderRadius: '999px' }} />
          <span style={{ fontSize: '12px', fontStyle: 'italic', fontWeight: 600, color: '#4b5068', lineHeight: 1.5 }}>"{style.quote}"</span>
        </div>
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="ss-report" id="ss-report-content">
        <link href="https://api.fontshare.com/v2/css?f[]=satoshi@300,400,500,700,900&display=swap" rel="stylesheet" />
        <style>{`
          /* ── Fluid design tokens ── */
          :root {
            --text-xs:   clamp(0.7rem,   0.65rem + 0.3vw,  0.875rem);
            --text-sm:   clamp(0.8rem,   0.75rem + 0.35vw, 1rem);
            --text-base: clamp(0.875rem, 0.82rem + 0.3vw,  1.125rem);
            --text-lg:   clamp(1rem,     0.9rem  + 0.75vw, 1.4rem);
            --text-xl:   clamp(1.25rem,  1rem    + 1.1vw,  2rem);
            /* Fluid spacing: clamp(min, preferred, max) */
            --sp-2:  clamp(0.35rem, 0.5vw,  0.5rem);
            --sp-3:  clamp(0.5rem,  0.75vw, 0.75rem);
            --sp-4:  clamp(0.6rem,  1vw,    1rem);
            --sp-6:  clamp(0.9rem,  1.5vw,  1.5rem);
            --sp-8:  clamp(1rem,    2vw,    2rem);
            --sp-10: clamp(1.1rem,  2.5vw,  2.5rem);
            --sp-12: clamp(1.25rem, 3vw,    3rem);
            --sp-16: clamp(1.5rem,  4vw,    4rem);
            /* Legacy aliases so existing calc() references work */
            --space-4: var(--sp-4); --space-6: var(--sp-6); --space-8: var(--sp-8);
            --space-10: var(--sp-10); --space-12: var(--sp-12); --space-16: var(--sp-16);
            --navy: #0f2744; --red: #A81026; --gold: #D79A1E;
            --ink: #1f2937; --muted: #6b7280;
            --bg: #f4f6f8; --card: #ffffff; --line: #d9e0e7; --soft: #eef2f6;
            --radius: clamp(14px, 2vw, 20px); --radius-sm: clamp(10px, 1.5vw, 12px);
            --shadow: 0 18px 40px rgba(14,39,68,.10);
            --font-body: 'Satoshi', sans-serif;
            /* Logo panel: fluid width clamped between 180px and 500px */
            --logo-w: clamp(180px, 42vw, 500px);
            --logo-h: clamp(90px,  21vw, 250px);
            --logo-img-h: clamp(30px, 6vw, 65px);
            --logo-pl: clamp(20px, 5vw, 60px);
            /* Cover padding fluid */
            --cover-pad: clamp(1rem, 4vw, 4rem);
            /* Inner page padding */
            --page-pad: clamp(0.9rem, 3vw, 2.5rem);
          }

          /* ── Base ── */
          .ss-report {
            font-family: var(--font-body);
            background: linear-gradient(180deg, #eef4f9 0%, #f7fafc 100%);
            color: var(--ink);
            line-height: 1.5;
          }
          .ss-report .report {
            max-width: 1100px;
            margin: 0 auto;
            padding: var(--sp-8) clamp(0.4rem, 2vw, 1rem) var(--sp-16);
            box-sizing: border-box;
          }
          .ss-report .page {
            background: var(--card);
            border: 1px solid var(--line);
            border-radius: clamp(14px, 2.5vw, 28px);
            box-shadow: var(--shadow);
            overflow: hidden;
            margin-bottom: var(--sp-8);
          }

          /* ── Cover ── */
          .ss-report .cover {
            position: relative;
            background:
              radial-gradient(circle at top right, rgba(214,162,63,.22), transparent 28%),
              linear-gradient(135deg, ${T.navy}, ${T.blue} 55%, ${T.teal});
            color: white;
          }
          .ss-report .cover-inner {
            padding: var(--cover-pad);
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            min-height: clamp(480px, 92vh, 1200px);
            box-sizing: border-box;
          }
          /* Brand row: badge on left, logo panel absolutely positioned top-right */
          .ss-report .brand {
            display: flex;
            align-items: center;
            position: relative;
            z-index: 3;
            /* reserve space so badge doesn't hide under logo panel */
            padding-right: calc(var(--logo-w) * 0.35);
          }
          .ss-report .logo-panel {
            position: absolute;
            top: 0;
            right: 0;
            width: var(--logo-w);
            height: var(--logo-h);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1;
            pointer-events: none;
          }
          .ss-report .logo-panel-inner {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            position: relative;
            z-index: 2;
            padding-left: var(--logo-pl);
            padding-bottom: clamp(8px, 2vw, 25px);
          }
          .ss-report .logo-img {
            height: var(--logo-img-h);
            width: auto;
            object-fit: contain;
            flex-shrink: 0;
          }
          .ss-report .badge {
            display: inline-flex;
            align-items: center;
            border: 1px solid rgba(255,255,255,.18);
            background: rgba(255,255,255,.08);
            padding: clamp(.3rem, .8vw, .6rem) clamp(.5rem, 1.2vw, 1rem);
            border-radius: 999px;
            font-size: clamp(0.58rem, 1.5vw, 0.85rem);
            white-space: nowrap;
            flex-shrink: 0;
          }
          .ss-report .cover-title { margin-top: clamp(1rem, 5vw, 4rem); }
          .ss-report .cover-title h2 {
            margin: 0 0 var(--sp-4);
            font-size: clamp(1.5rem, 5vw, 4.8rem);
            line-height: 1.0;
            color: #FFFFFF;
            word-break: break-word;
          }
          .ss-report .cover-title p {
            font-size: clamp(0.82rem, 1.8vw, 1.15rem);
            max-width: 100%;
            color: rgba(255,255,255,.84);
            text-align: justify;
          }
          /* Cover grid: 3 cols → 2 cols → 1 col via auto-fill */
          .ss-report .cover-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(min(120px, 100%), 1fr));
            gap: clamp(0.4rem, 1.2vw, 1rem);
            margin-top: clamp(1rem, 3vw, 3rem);
          }
          .ss-report .meta {
            background: rgba(255,255,255,.08);
            border: 1px solid rgba(255,255,255,.16);
            border-radius: clamp(12px, 2vw, 18px);
            padding: clamp(0.5rem, 1.2vw, 1rem) clamp(0.6rem, 1.3vw, 1.1rem);
            min-width: 0;
          }
          .ss-report .meta span {
            display: block;
            font-size: var(--text-xs);
            color: rgba(255,255,255,.66);
            margin-bottom: .25rem;
          }
          .ss-report .meta strong {
            font-size: var(--text-base);
            color: white;
            word-break: break-word;
          }
          .ss-report .cover-badges {
            display: flex;
            flex-wrap: wrap;
            gap: clamp(.3rem, .8vw, .6rem);
            margin-top: var(--sp-8);
          }
          .ss-report .cover-badge {
            background: rgba(255,255,255,.12);
            border: 1px solid rgba(255,255,255,.18);
            padding: clamp(.25rem, .6vw, .5rem) clamp(.45rem, 1vw, .9rem);
            border-radius: 999px;
            font-size: clamp(0.65rem, 1.5vw, 0.95rem);
            white-space: nowrap;
          }
          .ss-report .footer-note {
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            gap: clamp(0.2rem, 1vw, 1rem);
            color: rgba(255,255,255,.72);
            font-size: clamp(0.65rem, 1.4vw, 0.82rem);
            margin-top: var(--sp-8);
          }

          /* ── Inner pages ── */
          .ss-report .page-inner { padding: var(--page-pad); }
          .ss-report .topbar {
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            align-items: flex-start;
            gap: var(--sp-4);
            margin-bottom: var(--sp-8);
          }
          .ss-report .section-kicker {
            color: var(--red);
            font-size: var(--text-xs);
            text-transform: uppercase;
            letter-spacing: .12em;
            font-weight: 700;
          }
          .ss-report .section-title {
            margin: .35rem 0 0;
            font-size: var(--text-xl);
            color: var(--navy);
          }
          .ss-report .summary-grid { display: grid; grid-template-columns: 1fr; gap: var(--sp-6); }
          .ss-report .card {
            background: linear-gradient(180deg, #fff 0%, #fbfcfd 100%);
            border: 1px solid var(--line);
            border-radius: var(--radius);
            padding: var(--sp-6);
          }
          .ss-report .card h3 { margin: 0 0 .75rem; font-size: var(--text-lg); color: var(--navy); }
          .ss-report p { text-align: justify; }
          .ss-report .lead { color: #334155; font-size: var(--text-base); margin: 0; text-align: justify; }
          .ss-report .pill {
            display: inline-flex;
            align-items: center;
            gap: .35rem;
            padding: clamp(.25rem, .6vw, .5rem) clamp(.45rem, 1vw, .85rem);
            border-radius: 999px;
            font-size: clamp(0.62rem, 1.4vw, 0.8rem);
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: .06em;
            white-space: nowrap;
            flex-shrink: 0;
          }
          .ss-report .pill.red { background: #fef2f2; color: var(--red); }

          /* ── Bars — fluid label column, no fixed px ── */
          .ss-report .bars { display: grid; gap: clamp(0.7rem, 1.5vw, 1.2rem); margin-top: 1rem; }
          .ss-report .bar-row {
            display: grid;
            grid-template-columns: clamp(70px, 22%, 200px) 1fr clamp(28px, 6%, 60px);
            gap: clamp(0.3rem, 1vw, 1rem);
            align-items: center;
            min-width: 0;
          }
          .ss-report .bar-label {
            font-size: clamp(0.72rem, 1.6vw, 0.95rem);
            font-weight: 700;
            color: var(--navy);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            min-width: 0;
          }
          .ss-report .track {
            height: clamp(14px, 2.5vw, 24px);
            background: #e9eef3;
            border-radius: 999px;
            overflow: hidden;
            position: relative;
            min-width: 0;
          }
          .ss-report .fill { height: 100%; border-radius: 999px; }
          .ss-report .fill.sig  { background: linear-gradient(90deg, #1e3a8a, #3b82f6); }
          .ss-report .fill.stim { background: linear-gradient(90deg, #14532d, #22c55e); }
          .ss-report .fill.sug  { background: linear-gradient(90deg, #b45309, #f59e0b); }
          .ss-report .fill.shar { background: linear-gradient(90deg, #4c1d95, #a78bfa); }
          .ss-report .fill.adpt { background: linear-gradient(90deg, #4c1d95, #c4b5fd); }
          .ss-report .score-box { text-align: right; font-variant-numeric: tabular-nums; min-width: 0; }
          .ss-report .score-box strong { display: block; color: var(--navy); font-size: clamp(0.78rem, 1.6vw, 1.1rem); }
          .ss-report .small { font-size: var(--text-xs); color: var(--muted); }

          /* ── Style definition list ── */
          .ss-report .ss-list { display: grid; gap: 1.25rem; }
          .ss-report .ss-item {
            border-radius: 16px;
            padding: clamp(0.75rem, 2vw, 1.25rem);
            background: #fff;
          }
          /* Cognitive/behavior & strength/growth side-by-side → single column on narrow */
          .ss-report .ss-cols {
            display: flex;
            flex-wrap: wrap;
            gap: 1rem;
          }
          .ss-report .ss-cols > * { flex: 1 1 min(200px, 100%); min-width: 0; }

          /* ── Callout ── */
          .ss-report .callout {
            background: linear-gradient(135deg, #fff7f7, #fbeaea);
            border: 1px solid var(--line);
            border-radius: 18px;
            padding: clamp(0.9rem, 2vw, 1.25rem) clamp(1rem, 2.5vw, 1.5rem);
            color: #334155;
          }

          /* ── Adaptability table — stack to block on tiny screens ── */
          .ss-report .adapt-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 1rem;
            table-layout: fixed;
          }
          .ss-report .adapt-table th {
            font-size: clamp(8px, 1.2vw, 9px);
            font-weight: 800;
            letter-spacing: .09em;
            text-transform: uppercase;
            color: #4b5068;
            text-align: left;
            padding: clamp(6px, 1.2vw, 10px) clamp(8px, 1.5vw, 14px);
            border-bottom: 1px solid #e4e8f0;
            background: #f9fafb;
          }
          .ss-report .adapt-table td {
            padding: clamp(6px, 1.2vw, 10px) clamp(8px, 1.5vw, 14px);
            border-bottom: 1px solid #e4e8f0;
            font-size: clamp(0.75rem, 1.5vw, 0.88rem);
            color: #0d0e10;
            line-height: 1.6;
            vertical-align: top;
            word-break: break-word;
          }
          /* Score range col: auto width, wrapping allowed */
          .ss-report .adapt-table col:first-child { width: 30%; }
          .ss-report .adapt-table col:last-child  { width: 70%; }

          /* ── Back cover ── */
          .ss-report .back-cover { color: white; }
          .ss-report .back-cover .page-inner {
            display: flex;
            flex-direction: column;
            justify-content: space-between;
          }
          .ss-report .back-cover-header {
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            align-items: center;
            gap: 0.5rem;
            padding: clamp(0.9rem, 2.5vw, 1.5rem) clamp(0.9rem, 3vw, 2.5rem);
            border-bottom: 1px solid rgba(255,255,255,0.15);
          }
          .ss-report .closing h2 { margin: 0 0 1rem; font-size: clamp(1.4rem, 4vw, 3.25rem); color: #FFFFFF; }
          .ss-report .closing p  { max-width: 100%; color: rgba(255,255,255,.84); font-size: clamp(0.85rem, 2vw, 1.2rem); text-align: justify; }
          /* Contact grid: 3→2→1 cols automatically */
          .ss-report .contact-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(min(140px, 100%), 1fr));
            gap: clamp(0.4rem, 1.2vw, 1rem);
            margin-top: var(--sp-8);
          }
          .ss-report .contact {
            background: rgba(168,16,38,.1);
            border: 1px solid rgba(255,255,255,.16);
            border-radius: clamp(10px, 2vw, 16px);
            padding: clamp(0.6rem, 1.5vw, .9rem);
            min-width: 0;
          }
          .ss-report .contact span {
            display: block;
            color: rgba(255,255,255,.64);
            font-size: var(--text-xs);
            margin-bottom: .3rem;
          }
          .ss-report .contact strong { font-size: clamp(0.78rem, 1.6vw, 0.92rem); word-break: break-word; }
          .ss-report .light { color: rgba(255,255,255,.68); }

          @media print {
            .ss-report { background: white; }
            .ss-report .report { max-width: none; padding: 0; }
            .ss-report .page { box-shadow: none; border-radius: 0; margin: 0 0 12px; border: 1px solid #ddd; }
            #ss-pdf-btn { display: none !important; }
          }

          /* ── Download Button ── */
          #ss-pdf-btn {
            position: fixed;
            bottom: 2rem;
            right: 2rem;
            z-index: 9999;
            display: inline-flex;
            align-items: center;
            gap: 0.6rem;
            padding: 0.75rem 1.4rem;
            background: linear-gradient(135deg, #F3AC26, #D79A1E);
            color: white;
            border: none;
            border-radius: 999px;
            box-shadow:
              0 8px 24px rgba(168,16,38,0.35),
              0 0 14px rgba(243,173,38,0.45);
            cursor: pointer;
            font-family: var(--font-body);
            font-size: 0.9rem;
            font-weight: 700;
            transition: transform 0.18s ease, box-shadow 0.18s ease, opacity 0.18s ease;
          }
          #ss-pdf-btn:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 12px 32px rgba(168,16,38,0.45);
          }
          #ss-pdf-btn:active:not(:disabled) { transform: translateY(0); }
          #ss-pdf-btn:disabled { opacity: 0.72; cursor: not-allowed; }
          #ss-pdf-btn .btn-spinner {
            width: 16px; height: 16px;
            border: 2px solid rgba(255,255,255,0.35);
            border-top-color: white;
            border-radius: 50%;
            animation: spin 0.7s linear infinite;
            flex-shrink: 0;
          }
          @keyframes spin { to { transform: rotate(360deg); } }
        `}</style>

        {/* ── Floating PDF Download Button ── */}
        <button
          id="ss-pdf-btn"
          onClick={handleDownloadPDF}
          disabled={isGenerating}
          aria-label="Download PDF Report"
        >
          {isGenerating ? (
            <>
              <span className="btn-spinner" />
              Generating PDF…
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download PDF
            </>
          )}
        </button>

        <div className="report">

          {/* ══════════════════════════════════════════════════════
              PAGE 1 — COVER
          ══════════════════════════════════════════════════════ */}
          <section className="page cover">
            <div className="cover-inner">

              {/* Logo panel — matching VAK SVG shape */}
              <div className="logo-panel">
                <svg
                  width="100%" height="100%"
                  viewBox="0 0 500 250"
                  style={{ position: 'absolute', top: 0, right: 0, zIndex: 0 }}
                  preserveAspectRatio="none"
                >
                  <path d="M 60,8 L 500,8 L 500,188 L 150,253 Q 120,258 113,229 Z" fill="rgba(0,0,0,0.04)" />
                  <path d="M 60,4 L 500,4 L 500,184 L 150,249 Q 120,254 113,225 Z" fill="rgba(0,0,0,0.06)" />
                  <path d="M 60,0 L 500,0 L 500,180 L 150,245 Q 120,250 113,221 Z" fill="white" />
                </svg>
                <div className="logo-panel-inner">
                  <img src={Acumen_25_Years_LOGO} alt="Acumen Logo" className="logo-img" />
                </div>
              </div>

              <div className="brand">
                <div className="badge">Confidential Individual Report</div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: 1, position: 'relative', zIndex: 2 }}>
                <div className="cover-title">
                  <h2>5S Leadership <br></br> Style Assessment</h2>
                  <p>
                    This comprehensive assessment report presents an analysis of your leadership influence preferences using the 5S framework, helping identify how you most naturally lead, motivate, and guide others — and how adaptable your style is across different contexts and situations.
                  </p>
                </div>

                <div className="cover-grid">
                  <div className="meta"><span>Participant</span><strong>{name}</strong></div>
                  <div className="meta"><span>Assessment Date</span><strong>{dateStr}</strong></div>
                  <div className="meta"><span>Primary Style</span><strong>{dominantStyle}</strong></div>
                </div>

                <div className="cover-badges">
                  {['Assessment Summary', '5S Distribution', 'Adaptability Score', 'Style Definitions'].map((b) => (
                    <span key={b} className="cover-badge">{b}</span>
                  ))}
                </div>
              </div>

              <div className="footer-note" style={{ position: 'relative', zIndex: 2 }}>
                <div>Based on the 5S Leadership Influence Style Survey framework.</div>
                <div>12 questions distributed across four leadership modes.</div>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════════════════
              PAGE 2 — ASSESSMENT SUMMARY
          ══════════════════════════════════════════════════════ */}
          <section className="page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 2</div>
                  <h2 className="section-title">Assessment Summary</h2>
                </div>
                <div className="pill red">Dominant Style: {dominantStyle}</div>
              </div>

              <div className="summary-grid">
                <div className="card">
                  <h3>Understanding Your Leadership Style</h3>
                  <p className="lead">
                    While each leader has a primary mode through which they most naturally influence others, effective leadership requires the ability to flex across all four styles. The 5S framework identifies not just your dominant mode but your overall adaptability — the degree to which you adjust your approach based on context, audience, and situation.
                  </p>
                  <p className="lead" style={{ marginTop: '1rem' }}>
                    The survey you completed was designed to surface your predominant leadership influence preference. These scores reveal your natural style, providing actionable insight into how you build rapport, motivate others, and can deepen your effectiveness by recognising and matching the preferred styles of the people you lead.
                  </p>
                </div>

                <div className="card">
                  <h3>Why Style Awareness Matters</h3>
                  <p className="lead">
                    Research consistently shows that leaders who understand their own influence style — and can consciously adapt it — achieve stronger engagement, better communication outcomes, and higher team performance. Style awareness is not about changing who you are; it is about expanding your range so you can meet people where they are and lead more effectively in diverse contexts.
                  </p>
                </div>

                <div className="card">
                  <h3>Your 5S Distribution</h3>
                  <div className="bars">
                    <div className="bar-row">
                      <div className="bar-label">Signaling</div>
                      <div className="track"><div className="fill sig" style={{ width: sigPct }} /></div>
                      <div className="score-box"><strong>{sig}</strong></div>
                    </div>
                    <div className="bar-row">
                      <div className="bar-label">Stimulating</div>
                      <div className="track"><div className="fill stim" style={{ width: stimPct }} /></div>
                      <div className="score-box"><strong>{stim}</strong></div>
                    </div>
                    <div className="bar-row">
                      <div className="bar-label">Suggesting</div>
                      <div className="track"><div className="fill sug" style={{ width: sugPct }} /></div>
                      <div className="score-box"><strong>{sug}</strong></div>
                    </div>
                    <div className="bar-row">
                      <div className="bar-label">Sharing &amp; Sanctioning</div>
                      <div className="track"><div className="fill shar" style={{ width: sharPct }} /></div>
                      <div className="score-box"><strong>{shar}</strong></div>
                    </div>
                    <div className="bar-row" style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px dashed #e2e8f0' }}>
                      <div className="bar-label" style={{ fontSize: '0.88rem', color: '#6d28d9' }}>Adaptability Score</div>
                      <div className="track"><div className="fill adpt" style={{ width: adaptBarPct }} /></div>
                      <div className="score-box"><strong style={{ color: '#6d28d9' }}>{adapt > 0 ? `+${adapt}` : adapt}</strong></div>
                    </div>
                  </div>
                  <p className="small" style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                    Style scores reflect responses mapped to each mode (12 total). Adaptability ranges from −24 to +24.
                  </p>
                </div>

                <div className="callout" style={{ marginTop: '0.75rem' }}>
                  <strong style={{ color: '#A81026' }}>Your Result:</strong>
                  <p style={{ marginTop: '0.5rem' }}>{resultText}</p>
                </div>

                {/* Adaptability Range — score, quote, user's tier only */}
                <div style={{ marginTop: '1.25rem', background: '#fff', border: '1.5px solid #e4e8f0', borderRadius: '14px', overflow: 'hidden' }}>
                  {/* Score header */}
                  <div style={{ padding: 'clamp(10px,2.5vw,20px)', background: '#f9fafb', borderBottom: '1px solid #e4e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 'clamp(2rem,7vw,48px)', fontWeight: 700, lineHeight: 1, color: '#0d0e10' }}>
                        {adapt > 0 ? `+${adapt}` : adapt}
                      </span>
                      <span style={{ fontSize: 'clamp(11px,1.8vw,13px)', color: '#4b5068' }}>Adaptability Score</span>
                    </div>
                    <span style={tierLabelStyle}>{currentTierRow.pill}</span>
                  </div>
                  {/* Quote */}
                  <div style={{ padding: 'clamp(8px,1.8vw,14px) clamp(10px,2.5vw,20px)', borderBottom: '1px solid #e4e8f0' }}>
                    <div style={{ fontSize: 'clamp(11px,1.8vw,13px)', fontStyle: 'italic', fontWeight: 500, color: '#4b5068', lineHeight: 1.6, borderLeft: '3px solid #A81026', paddingLeft: '14px' }}>
                      "The best leaders don't stay at the top or bottom of the ladder. They move up and down depending on what the person needs."
                    </div>
                  </div>
                  {/* User's tier row only */}
                  <table className="adapt-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <colgroup><col /><col /></colgroup>
                    <thead>
                      <tr>
                        <th style={{ fontSize: '0.86rem', fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: '#4b5068', textAlign: 'left', padding: '10px 14px', borderBottom: '1px solid #e4e8f0', width: '190px' }}>
                          Score Range
                        </th>
                        <th style={{ fontSize: '0.86rem', fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: '#4b5068', textAlign: 'left', padding: '10px 14px', borderBottom: '1px solid #e4e8f0' }}>
                          What It Means
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={{ background: '#fffbeb' }}>
                        <td style={{ padding: '10px 14px', verticalAlign: 'top' }}>
                          <span style={{ display: 'inline-block', fontSize: '0.86rem', fontWeight: 700, padding: '2px 8px', borderRadius: '999px', background: currentTierRow.pb, color: currentTierRow.pc, border: `1px solid ${currentTierRow.border}`, wordBreak: 'break-word' }}>
                            {currentTierRow.pill}
                          </span>
                          <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#d97706', marginTop: '4px' }}>← You are here</div>
                        </td>
                        <td style={{ padding: '10px 14px', fontSize: '0.88rem', color: '#0d0e10', lineHeight: 1.6, verticalAlign: 'top' }}>
                          {currentTierRow.desc}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════════════════
              PAGE 3 — STYLE DEFINITIONS (Signaling & Stimulating)
          ══════════════════════════════════════════════════════ */}
          <section className="page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 3</div>
                  <h2 className="section-title">Leadership Style Definitions</h2>
                </div>
                <div className="pill red">Signaling &amp; Stimulating</div>
              </div>

              <div className="card" style={{ marginBottom: '1.5rem' }}>
                <h3>5S Parameters: Conceptual Definitions Across Roles and Levels</h3>
                <p className="lead">
                  The 5S Leadership framework describes how individuals naturally tend to influence, motivate, and guide those around them. Rather than assuming one style fits all situations, it acknowledges that effective leaders draw on different modes — and that awareness of your dominant style is the first step toward expanding your range.
                </p>
              </div>

              <div className="ss-list">
                {STYLE_DEFS_SORTED.slice(0, 2).map(renderStyleCard)}
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════════════════
              PAGE 4 — STYLE DEFINITIONS (Suggesting & Sharing & Sanctioning)
          ══════════════════════════════════════════════════════ */}
          <section className="page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 4</div>
                  <h2 className="section-title">Leadership Style Definitions</h2>
                </div>
                <div className="pill red" style={{ background: '#f5f3ff', color: '#6d28d9' }}>
                  Suggesting &amp; Sharing &amp; Sanctioning
                </div>
              </div>

              <div className="ss-list">
                {STYLE_DEFS_SORTED.slice(2, 4).map(renderStyleCard)}
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════════════════
              BACK COVER
          ══════════════════════════════════════════════════════ */}
          <section className="page back-cover">
            <div style={{
              background: `radial-gradient(circle at top right, rgba(214,162,63,.22), transparent 28%), linear-gradient(135deg,${T.navy},${T.blue} 55%,${T.teal})`,
              minHeight: '56vh',
            }}>
              {/* Header bar */}
              <div className="back-cover-header">
                <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '.12em', fontWeight: 700, color: 'rgba(255,255,255,0.66)' }}>
                  {/* Back Cover */}
                </div>
                <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', letterSpacing: '.05em' }}>
                  Acumen Business Catalyst
                </div>
              </div>

              <div className="page-inner">
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: 1 }}>
                  <div className="closing" style={{ padding: '0.1rem' }}>
                    <h2>Adaptability is the Edge</h2>
                    <p>
                      While you naturally lean toward a {dominantStyle} leadership style, the most effective leaders learn to flex across all four modes — reading what the situation, the team, and the moment requires. Use these insights to recognise the preferred styles of those you lead and adapt your approach to create deeper engagement, stronger trust, and better results.
                    </p>
                  </div>
                </div>

                <div>
                  <div className="card" style={{ marginTop: '1.5rem', background: 'rgba(255,255,255,.06)', borderColor: 'rgba(255,255,255,.14)', color: 'white' }}>
                    <h3 style={{ color: 'white' }}>Practical Leadership Design</h3>
                    <p className="lead" style={{ color: 'rgba(255,255,255,.84)' }}>{practicalLeadershipText}</p>
                  </div>

                  <div className="contact-grid">
                    <div className="contact"><span>Brand</span><strong>Acumen Business Catalyst</strong></div>
                    <div className="contact"><span>Focus</span><strong>Leadership • Culture • Capability</strong></div>
                    <div className="contact"><span>Report Type</span><strong>5S Leadership Style Report</strong></div>
                  </div>

                  <p className="light" style={{ marginTop: '1.5rem', fontSize: '0.75rem' }}>
                    This report is generated from the 5S Leadership Influence Style Survey framework.
                  </p>
                </div>
              </div>
            </div>
          </section>

        </div>
      </div>

      {/* ── Hidden PDF generator (mounts only while generating) ── */}
      {pdfData && (
        <Hidden5SReport
          participantData={pdfData}
          scriptsReady={scriptsReady}
          onComplete={() => { setPdfData(null); setIsGenerating(false); }}
        />
      )}
    </>
  );
};

export default FivesResult;